import React, { useState, useMemo } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPackage, FiAlertTriangle, FiSearch, FiFilter, FiTruck } = FiIcons;

export default function ProductTable({ products, suppliers = [], onEdit, onDelete, isAdmin, canEdit }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [stockFilter, setStockFilter] = useState('All');
  const [supplierFilter, setSupplierFilter] = useState('All');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const categories = useMemo(() => {
    const cats = new Set(products.map(p => p.category));
    return ['All', ...Array.from(cats)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || product.category === categoryFilter;
      
      let matchesStock = true;
      if (stockFilter === 'InStock') matchesStock = product.stock >= 5;
      if (stockFilter === 'Low') matchesStock = product.stock > 0 && product.stock < 5;
      if (stockFilter === 'Out') matchesStock = product.stock === 0;

      let matchesSupplier = true;
      if (supplierFilter === 'None') matchesSupplier = !product.supplier_id;
      else if (supplierFilter !== 'All') matchesSupplier = product.supplier_id === supplierFilter;

      return matchesSearch && matchesCategory && matchesStock && matchesSupplier;
    });
  }, [products, searchTerm, categoryFilter, stockFilter, supplierFilter]);

  const grandTotal = filteredProducts.reduce((acc, product) => acc + (product.price * product.stock), 0);

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-sm border border-gray-200 text-center">
        <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          <SafeIcon icon={FiPackage} className="text-3xl text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No hay productos</h3>
        <p className="text-gray-500">Aún no hay productos en el inventario.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-40 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiFilter} className="text-gray-400" />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors appearance-none bg-white cursor-pointer"
              >
                <option value="All">Categorías</option>
                {categories.filter(c => c !== 'All').map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div className="w-full sm:w-48 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiTruck} className="text-gray-400" />
              </div>
              <select
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors appearance-none bg-white cursor-pointer text-ellipsis"
              >
                <option value="All">Proveedores</option>
                <option value="None">Sin proveedor</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="w-full sm:w-40 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiFilter} className="text-gray-400" />
              </div>
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors appearance-none bg-white cursor-pointer"
              >
                <option value="All">Todo el stock</option>
                <option value="InStock">En stock (5+)</option>
                <option value="Low">Bajo stock (&lt;5)</option>
                <option value="Out">Agotado (0)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <SafeIcon icon={FiSearch} className="text-3xl text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron resultados</h3>
          <p className="text-gray-500">No hay productos que coincidan con tus filtros.</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('All');
              setStockFilter('All');
              setSupplierFilter('All');
            }}
            className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
                  <th className="px-6 py-4">Nombre</th>
                  <th className="px-6 py-4">Categoría</th>
                  <th className="px-6 py-4">Proveedor</th>
                  <th className="px-6 py-4">Precio</th>
                  <th className="px-6 py-4">Stock</th>
                  <th className="px-6 py-4">Total</th>
                  {canEdit && <th className="px-6 py-4 text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProducts.map((product) => {
                  const isLowStock = product.stock > 0 && product.stock < 5;
                  const isOutStock = product.stock === 0;
                  const totalValue = product.price * product.stock;
                  
                  const supplier = suppliers.find(s => s.id === product.supplier_id);
                  const supplierName = supplier ? supplier.name : '-';

                  return (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-900">{product.name}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {product.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm truncate max-w-[150px]" title={supplierName}>
                        {supplierName}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{formatCurrency(product.price)}</td>
                      <td className="px-6 py-4">
                        {isOutStock ? (
                          <div className="flex items-center text-red-600 font-semibold bg-red-50 px-2 py-1 rounded-md w-fit">
                            <SafeIcon icon={FiAlertTriangle} className="mr-1.5" />
                            Agotado
                          </div>
                        ) : isLowStock ? (
                          <div className="flex items-center text-orange-600 font-semibold bg-orange-50 px-2 py-1 rounded-md w-fit">
                            <SafeIcon icon={FiAlertTriangle} className="mr-1.5" />
                            {product.stock}
                          </div>
                        ) : (
                          <span className="text-gray-600">{product.stock}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        {formatCurrency(totalValue)}
                      </td>
                      
                      {canEdit && (
                        <td className="px-6 py-4 text-right space-x-2">
                          <button
                            onClick={() => onEdit(product)}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => onDelete(product.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredProducts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Valor Total Filtrado</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(grandTotal)}</p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-sm font-medium text-gray-500">Resultados</p>
            <p className="text-2xl font-semibold text-gray-700">{filteredProducts.length} productos</p>
          </div>
        </div>
      )}
    </div>
  );
}